import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';

import { IconBackSvg, IconCloseSvg } from '../../../assets/achievements';
import { Pressable } from '../common/Touchable';
import {
	ACHIEVEMENTS_FONT,
	achievementsColors,
	PANEL_WIDTH,
	RADIUS_FULL,
} from './achievementsTheme';
import { achievementText } from './achievementsViewModel';

/**
 * Figma "Header" (4488:19618 / 4488:19798) - the same 358x56 row on both modals: a round back
 * button, a centred title and a close control.
 */
export const HEADER_HEIGHT = 56;
const SIDE_PADDING = 8;
const BACK_SIZE = 44;
const BACK_ICON = 24;
const TITLE_WIDTH = 262;
const CLOSE_WIDTH = 40;
const CLOSE_ICON = 20;

type Props = {
	title: string;
	onBack: () => void;
	onClose: () => void;
	scale: number;
};

export const AchievementsHeader = ({ title, onBack, onClose, scale }: Props) => {
	const styles = useMemo(() => createStyles(scale), [scale]);
	const { t } = useTranslation();

	return (
		<View style={styles.header}>
			<Pressable
				accessibilityRole='button'
				accessibilityLabel={achievementText(t, 'T_SITE_ACHIEVEMENTS_BACK')}
				onPress={onBack}
				style={styles.back}
			>
				{/* Wrapped for the same painting-order reason as the inventory tabs' icons. */}
				<View>
					<IconBackSvg width={BACK_ICON * scale} height={BACK_ICON * scale} />
				</View>
			</Pressable>

			<Text numberOfLines={1} style={styles.title}>
				{title}
			</Text>

			<Pressable
				accessibilityRole='button'
				accessibilityLabel={achievementText(t, 'T_SITE_ACHIEVEMENTS_CLOSE')}
				onPress={onClose}
				style={styles.close}
			>
				<View>
					<IconCloseSvg width={CLOSE_ICON * scale} height={CLOSE_ICON * scale} />
				</View>
			</Pressable>
		</View>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		header: {
			width: PANEL_WIDTH * scale,
			height: HEADER_HEIGHT * scale,
			flexDirection: 'row',
			alignItems: 'center',
			paddingHorizontal: SIDE_PADDING * scale,
		},
		back: {
			width: BACK_SIZE * scale,
			height: BACK_SIZE * scale,
			alignItems: 'center',
			justifyContent: 'center',
			borderRadius: RADIUS_FULL * scale,
			backgroundColor: achievementsColors.sunken,
			overflow: 'hidden',
		},
		// Figma "Heading/H2": Sofia Sans Bold 22, line-height 1.3, gold/50.
		title: {
			width: TITLE_WIDTH * scale,
			color: achievementsColors.gold50,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 22 * scale,
			fontWeight: '700',
			lineHeight: 22 * 1.3 * scale,
			textAlign: 'center',
		},
		close: {
			width: CLOSE_WIDTH * scale,
			height: BACK_SIZE * scale,
			alignItems: 'center',
			justifyContent: 'center',
		},
	});

export default AchievementsHeader;
